import React, { useState } from 'react';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import {
  Divider,
  SizedBox,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Pagination } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import messages from './messages';

const tableGridSx = {
  maxHeight: 300,
  overflowY: 'auto',
  maxWidth: '100%',
  overflowX: 'auto',
};

const pageSize = 10;

/**
 * Renders Move-Out Date with Transfer Indicator using Chip component
 * Chip size: 53px width × 16px height (Hug content)
 */
const renderMoveOutDateWithTransfer = (rowData, formatMessage) => {
  const moveOutDate = rowData.moveOutDate;

  // No move-out date → Show "---"
  if (!moveOutDate) {
    return '---';
  }

  const formattedDate = formatDateDisplayLocal(moveOutDate);
  const isTransfer = Boolean(rowData.isTransfer);

  // Non-transfer move-out
  if (!isTransfer) {
    return formattedDate;
  }

  // Transfer move-out with Chip - exact Figma specs
  const chipLabel = formatMessage(messages.transfer);

  return (
    <span aria-label={`Move-out date with transfer: ${formattedDate}`}>
      {formattedDate}{' '}
      <BadgeStatus variant="filled" size="small" status="inProcess">
        {chipLabel}
      </BadgeStatus>
    </span>
  );
};

const ResidentHistoryTable = ({
  rows = [],
  onResidentClick,
  intl,
  showFloorPlanAtMoveOut = false,
}) => {
  const formatMessage = intl?.formatMessage;
  const [page, setPage] = useState(1);
  if (!rows || rows.length === 0) {
    return (
      <Typography textAlign="center">
        {formatMessage?.(messages.emptyStateMessage)}
      </Typography>
    );
  }
  const handleChange = (event, value) => {
    setPage(value);
  };
  const paginationProps = {
    count: pageSize,
    currentPage: page,
    intl,
    onPageChange: handleChange,
    pageCount: Math.ceil(rows.length / pageSize),
    perPage: pageSize,
    totalCount: rows.length,
  };

  const pageRows = rows.slice(
    (paginationProps.currentPage - 1) * paginationProps.perPage,
    paginationProps.currentPage * paginationProps.perPage,
  );

  return (
    <Grid container role="table" aria-label="Unit History Table">
      <Grid
        container
        paddingX={2}
        role="row"
        aria-label="Unit History Table Header Row"
      >
        <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.priorResident)}
          </Typography>
        </Grid>
        <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.moveInDate)}
          </Typography>
        </Grid>
        <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.moveOutDate)}
          </Typography>
        </Grid>
        {showFloorPlanAtMoveOut && (
          <Grid item xs={3} role="cell">
            <Typography variant="labelLarge">
              {formatMessage?.(messages.floorPlanAtMoveOut)}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <SizedBox h={5} />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid
        container
        role="rowgroup"
        aria-label="Resident History Table Rows"
        marginTop={1}
        paddingX={2}
        sx={tableGridSx}
      >
        {pageRows.map((rowData, i) => (
          <Grid container key={i} role="row">
            <Grid item xs={12}>
              <SizedBox h={5} />
            </Grid>
            <Grid container alignItems="center">
              <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
                <Typography
                  variant="labelMedium"
                  fontSize={14}
                  role="button"
                  sx={{ color: 'primary.main', cursor: 'pointer' }}
                  onClick={() => {
                    onResidentClick?.(rowData.residentId);
                  }}
                >
                  {rowData.residentName}
                </Typography>
              </Grid>
              <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
                <Typography variant="bodySmall">
                  {rowData.moveInDate
                    ? formatDateDisplayLocal(rowData.moveInDate)
                    : '---'}
                </Typography>
              </Grid>
              <Grid item xs={showFloorPlanAtMoveOut ? 3 : 4} role="cell">
                <Typography variant="bodySmall">
                  {renderMoveOutDateWithTransfer(rowData, formatMessage)}
                </Typography>
              </Grid>
              {showFloorPlanAtMoveOut && (
                <Grid item xs={3} role="cell">
                  <Typography variant="bodySmall">
                    {rowData.floorPlanName || '---'}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <SizedBox h={5} />
            </Grid>
          </Grid>
        ))}
      </Grid>
      {/* {Only show if pagination is needed} */}
      {rows.length > pageSize && (
        <Grid item xs={12}>
          <Pagination {...paginationProps}></Pagination>
        </Grid>
      )}
    </Grid>
  );
};

export default ResidentHistoryTable;
